import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory2';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Avatar } from '@/atoms/Avatar';
import { Badge } from '@/atoms/Badge';
import { Chip } from '@/atoms/Chip';
import { IconButton } from '@/atoms/IconButton';
import { Typography } from '@/atoms/Typography';
import { Search } from '@/molecules/Search';
import { Card, CardHeader, CardContent } from '@/organisms/Card';
import { DataTable, type MRT_ColumnDef } from '@/organisms/Table';
import { DashboardLayout } from '@/templates/DashboardLayout';
import type { NavItem } from '@/organisms/LeftNavigation';

/**
 * DashboardExample
 * Example page showing how to assemble a security dashboard from the design
 * system: DashboardLayout shell + a recent-findings Table.
 */
interface Finding {
  asset: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  rule: string;
  age: string;
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { id: 'inventory', label: 'Inventory', icon: <InventoryIcon /> },
  { id: 'threats', label: 'Threats', icon: <SecurityIcon /> },
  { id: 'alerts', label: 'Alerts', icon: <WarningIcon /> },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
];

const findings: Finding[] = [
  { asset: 'prod-db-01', severity: 'critical', rule: 'Public RDS snapshot', age: '2m' },
  { asset: 'api-gateway', severity: 'high', rule: 'Overly permissive SG', age: '14m' },
  { asset: 's3-backups', severity: 'medium', rule: 'Unencrypted bucket', age: '1h' },
  { asset: 'iam-role-ci', severity: 'low', rule: 'Unused access key', age: '3h' },
];

export function DashboardExample() {
  const [selected, setSelected] = useState('dashboard');
  const columns = useMemo<MRT_ColumnDef<Finding>[]>(
    () => [
      { accessorKey: 'asset', header: 'Asset' },
      {
        accessorKey: 'severity',
        header: 'Severity',
        Cell: ({ cell }) => (
          <Chip purpose="severity" level={cell.getValue<Finding['severity']>()} size="small" />
        ),
      },
      { accessorKey: 'rule', header: 'Rule' },
      { accessorKey: 'age', header: 'Age' },
    ],
    [],
  );

  return (
    <DashboardLayout
      navItems={navItems}
      selectedNavId={selected}
      onNavSelect={setSelected}
      toolbar={
        <>
          <Box sx={{ width: 320 }}>
            <Search placeholder="Search assets, findings..." />
          </Box>
          <Badge badgeContent={5} color="error">
            <IconButton label="notifications" icon={<NotificationsIcon />} />
          </Badge>
          <Avatar sx={{ width: 32, height: 32 }}>AS</Avatar>
        </>
      }
    >
      <Typography variant="h4" gutterBottom sx={{ fontFamily: 'inherit' }}>
        Security overview
      </Typography>
      <Card>
        <CardHeader title="Recent findings" />
        <CardContent sx={{ p: 0 }}>
          <DataTable columns={columns} data={findings} />
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
